"use client";

import { CalendarDays, MapPin } from "@/components/icons";
import { QuantityStepper } from "@/components/cart/quantity-stepper";
import { DeliveryWindowPicker } from "@/components/cart/delivery-window-picker";
import { formatNumber } from "@/lib/formatting/numbers";
import {
  earliestSelectableDeliveryDate,
  formatDeliveryWindow,
} from "@/lib/checkout/delivery-window";
import type { DeliveryLocation } from "@/lib/checkout/delivery-location";
import { Button } from "@/components/ui/button";
import { DockOnSiteField } from "@/components/cart/delivery-fields";
import { useListingBuyState } from "./listing-buy-state";

const WINDOW_LABEL_ID = "listing-delivery-window-label";
const WINDOW_VALUE_ID = "listing-delivery-window-value";
const ADDRESS_LABEL_ID = "listing-delivery-address-label";

export function ListingBookingBlock({
  unitName,
  unitNamePlural,
}: {
  unitName: string;
  unitNamePlural: string;
}) {
  return (
    <div className="space-y-4">
      <ListingQuantityRow unitName={unitName} unitNamePlural={unitNamePlural} />
      <ListingDeliveryAddressRow />
      <ListingDeliveryWindowRow />
      <ListingDockToggle />
    </div>
  );
}

function ListingQuantityRow({
  unitName,
  unitNamePlural,
}: {
  unitName: string;
  unitNamePlural: string;
}) {
  const {
    quantity,
    setQuantity,
    minQuantity,
    checkoutMinimum,
    setQuantityPending,
  } = useListingBuyState();

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold">Quantity</p>
          <p className="truncate text-sm text-muted-foreground">
            {formatNumber(quantity)}{" "}
            {quantity === 1 ? unitName : unitNamePlural}
          </p>
        </div>
        <QuantityStepper
          size="lg"
          quantity={quantity}
          minQuantity={minQuantity}
          onUpdateQuantity={(next) => {
            setQuantityPending(false);
            setQuantity(next);
          }}
          onPendingChange={setQuantityPending}
          className="shrink-0"
        />
      </div>
      {quantity < checkoutMinimum ? (
        <p className="text-sm text-muted-foreground">
          Below the {formatNumber(checkoutMinimum)}-unit checkout minimum.
          Request availability instead.
        </p>
      ) : null}
    </div>
  );
}

function formatDeliveryAddress(location: DeliveryLocation) {
  const street = [location.line1, location.line2].filter(Boolean).join(", ");
  return `${street}, ${location.city}, ${location.state} ${location.zip}`;
}

function ListingDeliveryAddressRow() {
  const { delivery, openAddressDialog } = useListingBuyState();
  const location = delivery.location;

  if (!location?.line1) {
    return (
      <Button
        type="button"
        variant="outline"
        className="w-full justify-start"
        onClick={() => openAddressDialog("edit")}
      >
        <MapPin className="size-4" />
        Add delivery address
      </Button>
    );
  }

  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex min-w-0 items-start gap-2">
        <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0">
          <p id={ADDRESS_LABEL_ID} className="text-sm font-semibold">
            Deliver to
          </p>
          <p className="text-sm text-muted-foreground">
            {formatDeliveryAddress(location)}
          </p>
        </div>
      </div>
      <Button
        type="button"
        variant="link"
        size="bare"
        aria-describedby={ADDRESS_LABEL_ID}
        onClick={() => openAddressDialog("edit")}
      >
        Change
      </Button>
    </div>
  );
}

function ListingDeliveryWindowRow() {
  const { deliveryWindow, setDeliveryWindow, leadTimeDays } =
    useListingBuyState();
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-2">
        <CalendarDays className="size-4 shrink-0 text-muted-foreground" />
        <p id={WINDOW_LABEL_ID} className="text-sm font-semibold">
          Delivery window
        </p>
      </div>
      <DeliveryWindowPicker
        window={deliveryWindow}
        onWindowChange={setDeliveryWindow}
        labelledBy={WINDOW_LABEL_ID}
        earliestDate={earliestSelectableDeliveryDate(leadTimeDays)}
      >
        <Button
          type="button"
          variant="link"
          size="bare"
          aria-labelledby={`${WINDOW_LABEL_ID} ${WINDOW_VALUE_ID}`}
        >
          <span id={WINDOW_VALUE_ID}>
            {formatDeliveryWindow(deliveryWindow)}
          </span>
        </Button>
      </DeliveryWindowPicker>
    </div>
  );
}

function ListingDockToggle() {
  const { hasDockOnSite, setHasDockOnSite } = useListingBuyState();
  return (
    <DockOnSiteField
      hasDockOnSite={hasDockOnSite}
      onHasDockOnSiteChange={setHasDockOnSite}
    />
  );
}
